import React from 'react';
import {
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  VStack,
} from '@chakra-ui/react';

interface PayrollSettingsFormProps {
  data: Record<string, any>;
  options: Record<string, any[]>;
  onChange: (data: Record<string, any>) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export default function PayrollSettingsForm({
  data,
  options,
  onChange,
  onSubmit,
  isSubmitting,
}: PayrollSettingsFormProps) {
  const set = (key: string, value: any) => onChange({ ...data, [key]: value });

  return (
    <VStack align="stretch" spacing={3}>
      <SimpleGrid columns={2} spacing={3}>
        <FormControl>
          <FormLabel fontSize="xs">Payday</FormLabel>
          <Select
            size="sm"
            value={data.payday ?? ''}
            onChange={(e) => set('payday', e.target.value)}
          >
            <option value="">—</option>
            {options.paydays?.map((p: any) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="xs">Delivery Method</FormLabel>
          <Select
            size="sm"
            value={data.delivery_method ?? ''}
            onChange={(e) => set('delivery_method', e.target.value)}
          >
            <option value="">—</option>
            {options.delivery_methods?.map((d: any) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="xs">Service Tier</FormLabel>
          <Select
            size="sm"
            value={data.customer_service_tier ?? ''}
            onChange={(e) => set('customer_service_tier', e.target.value)}
          >
            <option value="">—</option>
            {options.customer_service_tiers?.map((t: any) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="xs">Last Payroll Provider</FormLabel>
          <Input
            size="sm"
            value={data.last_payroll_provider ?? ''}
            onChange={(e) => set('last_payroll_provider', e.target.value)}
          />
        </FormControl>
      </SimpleGrid>

      <SimpleGrid columns={3} spacing={2}>
        {[
          ['cutoff', 'Cutoff'],
          ['paid_employees_w2_this_year', 'Paid W2 This Year'],
          ['has_itin_employees', 'ITIN Employees'],
          ['has_safety_program', 'Safety Program'],
          ['has_deductions_garnishments', 'Deductions/Garnishments'],
          ['has_health_insurance', 'Health Insurance'],
          ['has_life_insurance', 'Life Insurance'],
          ['has_401k_benefits', '401(k) Benefits'],
          ['has_certified_payroll', 'Certified Payroll'],
          ['has_time_attendance', 'Time & Attendance'],
          ['has_pto', 'PTO'],
        ].map(([key, label]) => (
          <Checkbox
            key={key}
            size="sm"
            isChecked={data[key] ?? false}
            onChange={(e) => set(key, e.target.checked)}
          >
            {label}
          </Checkbox>
        ))}
      </SimpleGrid>

      <Button
        colorScheme="purple"
        size="sm"
        onClick={onSubmit}
        isLoading={isSubmitting}
        alignSelf="flex-start"
      >
        Apply Payroll Settings
      </Button>
    </VStack>
  );
}
